import { ReactNode, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import UnsubscribeModal from 'components/Payment/Modals/UnsubscribeModal/UnsubscribeModal';

import ClientsService from 'features/client/clientService';
import { fetchJobsData } from 'features/jobs/jobsThunks';

import { useAppDispatch } from 'hooks/useTypedRedux';

import { notify } from 'utils/toast/createToast';

import { AlertType } from 'constants/enums';
import { DEBOUNCE_JOBS_TIMEOUT_MS } from 'constants/uiSettings';
import useErrorCatch from 'hooks/useErrorCatch';

interface UseJobseekerActionsProps {
  jobId: string;
  setActiveComponent: React.Dispatch<React.SetStateAction<ReactNode>>;
}

const useJobActions = ({
  jobId,
  setActiveComponent
}: UseJobseekerActionsProps): {
  unsubscribeFromJob: () => void;
  reactivateJob: () => void;
} => {
  const { t } = useTranslation();
  const catchError = useErrorCatch();
  const dispatch = useAppDispatch();

  const [isLoading, setIsLoading] = useState(false);

  const debouncedFetchJobs = useCallback(() => {
    setTimeout(() => {
      dispatch(fetchJobsData());
    }, DEBOUNCE_JOBS_TIMEOUT_MS);
  }, [dispatch]);

  const handleUnsubscribe = useCallback(async () => {
    try {
      setIsLoading(true);
      await ClientsService.unsubscribePaidJob(jobId);

      notify({
        type: AlertType.Success,
        message: t('payment.activationSuccess')
      });

      setActiveComponent(null);
      debouncedFetchJobs();
    } catch (error) {
      notify({
        type: AlertType.Error,
        message: t('error.message.cancelSubscription')
      });
    } finally {
      setIsLoading(false);
    }
  }, [jobId, setActiveComponent, debouncedFetchJobs, setIsLoading]);


  const handleReactivate = useCallback(async () => {
    if (!jobId) {
      return;
    }

    try {
      setIsLoading(true);
      await ClientsService.subscribePaidJob(jobId);
      
      notify({
        type: AlertType.Success,
        message: t('sidebar.job.notify.jobActive')
      });
      debouncedFetchJobs();
    } catch (error) {
      catchError({
        error,
        default: {
          message: t('error.message.operationFailed'),
          description: t('error.message.failedToReactivateJob')
        }
      });
    } finally {
      setIsLoading(false);
    }
  }, [jobId, debouncedFetchJobs, catchError, t]);

  const handleCancelUnsubscribe = useCallback(() => {
    setActiveComponent(null);
  }, [setActiveComponent]);

  const JobUnsubscribeComponent = useCallback(
    () => (
      <UnsubscribeModal
        open
        onCancel={handleCancelUnsubscribe}
        onUnsubscribe={handleUnsubscribe}
        isLoading={isLoading}
      />
    ),
    [handleUnsubscribe, handleCancelUnsubscribe, isLoading]
  );

  const unsubscribeFromJob = useCallback(() => {
    setActiveComponent(JobUnsubscribeComponent);
  }, [JobUnsubscribeComponent, setActiveComponent]);


  return { unsubscribeFromJob, reactivateJob: handleReactivate };
};

export default useJobActions;
